import { Code, ConnectError } from '@bufbuild/connect';
import { PartialMessage, Timestamp } from '@bufbuild/protobuf';
import { Box, Button, Typography, useMediaQuery } from '@mui/material';
import { useEffect, useRef, useState } from 'react';

import {
  parseIdParam,
  ContentDetailCard,
  HwProceedButton,
  InfoBar,
  InstructionsPopup,
  NoContentCard,
  PrimaryButton,
  SecondaryButton,
  IStyles,
  pxToRem,
  pxTovW,
  theme,
  ChapterSelectedCard,
  getMediaBasePath,
  deserify,
  getResourceCategory,
  formatSecondsToDateTimeString,
  isProjectDeadlinePassed,
  resourceTypeName,
  useCommonServiceClientContext,
} from '@geneo2-web/shared-ui';
import { AssignmentConfigEnum, CloudUpload, SessionModeEnum, SessionStatusEnum, TaskStudentAttemptStatusEnum } from '@protos/learning_management/lms.db_pb';
import { useGlobalContext } from 'apps/student/src/app/Context/GlobalContextProvider';
import { useDownloadContext } from 'apps/student/src/app/Context/DownloadContextProviderV2';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  Manage_Student_Projects,
  Student_Gil_Project_Report,
} from 'apps/student/src/routeHandling/RoutesNomenclature';
import { ProfileRolesEnum } from '@protos/user_management/ums.db_pb';
import { updateAssessmentContentWithMarks } from '../../Homework/utils_homework/homeworkUtil';
import {
  setActiveHomeworkContent,
  setActiveHomeworkStudentResponse,
  setActiveProjectSessionId,
  setPreviousProjectSessionInfo,
  setProjectEndPath,
} from '../reducer/labaroatory.slice';
import { useAppSelector } from 'apps/student/src/reduxStore/reduxHooks';
import { navigateToProjectResource } from '../utils';
import { setToastInfo } from '../../Home/reducer/homeDashboard.slice';
import { interactionEvent } from '../../Auth/Login/login_logout.events';
import { ProjectEvidencePopup } from './ProjectEvidencePopup';
import { ActiveProjectDetailsShimmer } from './shimmer';

/* -------------------------------------------------------------------------- */
/*                                  STYLES                                    */
/* -------------------------------------------------------------------------- */

const styles: IStyles = {
  root: {
    width: '100%',
    paddingY: {
      xs: pxToRem(20),
      md: pxToRem(30), lg: pxTovW(40),
    },
    boxSizing: 'border-box',
    overflowX: 'hidden',
    paddingX: {
      xs: pxToRem(20),
      lg: 0,
    },

    p: {
      xs: pxToRem(20),
      lg: `${pxTovW(30)} ${pxTovW(240)} ${pxTovW(100)}`,
    },
  },

  header: {
    display: 'flex',
    flexDirection: {
      xs: 'column',
      md: 'row',
    },
    gap: {
      xs: pxToRem(20),
      md: pxToRem(24), lg: pxTovW(30),
    },
    paddingBottom: {
      xs: pxToRem(20),
      md: pxToRem(21), lg: pxTovW(25),
    },
    justifyContent: {
      xs: 'flex-start',
      md: 'space-between',
    },
    margin: '0 auto',
    alignItems: {
      md: 'center',
    },
  },

  projectImage: {
    width: {
      xs: '100%',
      md: pxToRem(114), lg: pxTovW(180),
    },
    height: {
      xs: pxToRem(180),
      md: pxToRem(90), lg: pxTovW(140),
    },
    borderRadius: pxToRem(12),
    objectFit: 'cover',
    flexShrink: 0,
  },

  projectInfo: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: pxToRem(8),
  },

  projectTitle: {
    fontSize: {
      xs: pxToRem(22),
      md: pxToRem(23), lg: pxTovW(28),
    },
    fontWeight: 700,
  },

  metaContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: {
      xs: pxToRem(10),
      md: pxToRem(18), lg: pxTovW(20),
    },
    marginTop: pxToRem(8),
  },

  metaItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: pxToRem(2),
  },

  metaLabel: {
    fontSize: pxToRem(12),
    color: '#777777',
  },

  metaValue: {
    fontSize: pxToRem(14),
    fontWeight: 600,
  },

  status: {
    display: 'inline-flex',
    width: 'fit-content',
    padding: `${pxToRem(5)} ${pxToRem(12)}`,
    borderRadius: pxToRem(20),
    backgroundColor: '#E8F7EE',
    color: '#16803C',
    fontSize: pxToRem(12),
    fontWeight: 600,
  },

  statusEnded: {
    display: 'inline-flex',
    width: 'fit-content',
    padding: `${pxToRem(5)} ${pxToRem(12)}`,
    borderRadius: pxToRem(20),
    backgroundColor: '#F2F2F2',
    color: '#666666',
    fontSize: pxToRem(12),
    fontWeight: 600,
  },

  bodySection: {
    display: 'flex',
    gap: {
      xs: pxToRem(20),
      md: pxToRem(42), lg: pxTovW(60),
    },
    flexDirection: {
      xs: 'column',
      md: 'row',
    },
    margin: '0 auto',
    justifyContent: {
      xs: 'flex-start',
      md: 'space-between',
    },
  },

  leftPanel: {
    width: {
      xs: '100%',
      md: '45%',
      lg: pxTovW(590),
    },
    flexBasis: {
      md: '45%',
      lg: pxTovW(590),
    },
    justifyContent: 'center',
  },

  descriptionBox: {
    backgroundColor: '#FFFFFF',
    border: '1px solid #EEEEEE',
    borderRadius: pxToRem(10),
    padding: {
      xs: pxToRem(16),
      md: pxToRem(21), lg: pxTovW(24),
    },
    boxShadow: `0px 3px ${pxToRem(20)} #0000000F`,
    marginTop: { xs: pxToRem(18), lg: pxTovW(20) },
  },

  instructionBox: {
    backgroundColor: '#FFFFFF',
    border: '1px solid #EEEEEE',
    borderRadius: pxToRem(10),
    padding: {
      xs: pxToRem(16),
      md: pxToRem(21), lg: pxTovW(24),
    },
    marginTop: { xs: pxToRem(18), lg: pxTovW(20) },
    boxShadow: `0px 3px ${pxToRem(20)} #0000000F`,
  },

  instructionItem: {
    display: 'flex',
    gap: pxToRem(10),
    marginTop: pxToRem(12),
  },

  instructionNumber: {
    width: pxToRem(24),
    height: pxToRem(24),
    borderRadius: '50%',
    backgroundColor: '#F2F2FF',
    color: '#475D99',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: pxToRem(12),
    fontWeight: 600,
    flexShrink: 0,
  },

  rightPanel: {
    width: {
      md: '55%',
      lg: pxTovW(727),
    },
  },

  cardsContainer: {
    width: {
      md: '100%',
      lg: pxTovW(727),
    },
    paddingTop: { xs: pxToRem(18), lg: pxTovW(20) },
    display: 'flex',
    flexDirection: 'column',
    gap: {
      xs: pxToRem(20),
      md: pxToRem(18), lg: pxTovW(20),
    },
  },

  dateBox: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: pxToRem(10),
    marginTop: pxToRem(20),
  },

  dateItem: {
    flex: 1,
    padding: pxToRem(12),
    backgroundColor: '#F8F8FC',
    borderRadius: pxToRem(8),
  },

  mobileButton: {
    position: 'fixed',
    bottom: 10,
    left: 10,
    right: 10,
    zIndex: 10,
  },
};

/* -------------------------------------------------------------------------- */
/*                           RESOURCE ICON DETAILS                            */
/* -------------------------------------------------------------------------- */

const getIconDetails = (resource: {
  estimatedTimeInMin: number;
  resourceType: string;
}) => [
    {
      iconName: 'clock',
      text: `${resource.estimatedTimeInMin} Min`,
    },
    {
      iconName: resource.resourceType === 'Video' ? 'video' : 'questions',
      text: resource.resourceType,
    },
  ];

/* -------------------------------------------------------------------------- */
/*                       STUDENT PROJECT DETAILS                              */
/* -------------------------------------------------------------------------- */

export const ActiveProjectDetails = () => {
  const largeScreen = useMediaQuery(theme.breakpoints.up('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { setSelectedFunction, setSelectedLogoClickHandler } = useGlobalContext();
  const { isOffline } = useDownloadContext();
  const homeworkId =
    new URLSearchParams(location.search).get('assessmentId') || undefined;

  const {
    active_homework_content,
    active_homework_student_response,
    active_project_session_id,
    previous_project_session_info,
  } = deserify(useAppSelector((state) => state.laboratory));

  const activeProjectContent =
    active_homework_content && homeworkId
      ? active_homework_content[homeworkId]
      : undefined;

  const activeProjectStudentResponse =
    active_homework_student_response && homeworkId
      ? active_homework_student_response[homeworkId]
      : undefined;

  const { userInfo } = deserify(useAppSelector((state) => state.auth));
  const studentId = userInfo?.studentProfileId;
  const {
    LmsAssessmentStudentApiServiceV1ClientWithStatusCodeHandler,
    LmsAssessmentCommonAPIServiceV1ClientWithStatusCodeHandler,
  } = useCommonServiceClientContext();

  const assessment = activeProjectContent?.assessment;
  const projectSessionId = active_project_session_id;
  const completedResourceIds = previous_project_session_info?.completedResourceIds || [];
  const creatingProjectSessionRef = useRef(false);
  const [loading, setLoading] = useState<'loading' | 'completed' | 'error'>(
    'loading',
  );
  const [submittingProject, setSubmittingProject] = useState(false);
  const [projectSubmitted, setProjectSubmitted] = useState(false);
  const [evidencePopupOpen, setEvidencePopupOpen] = useState(false);
  const [learningOutcomesOpen, setLearningOutcomesOpen] = useState(false);
  //~ The union of the project's resources' outcomes, in resource order.
  const learningOutcomes = activeProjectContent?.learningOutcomes ?? [];

  const projectResources = (activeProjectContent?.projectResources ?? []).map(
    (resource, index) => ({
      id: Number(resource.resourceId || index + 1),
      resourceId: resource.resourceId || `${index + 1}`,
      title: resource.title || `Resource ${index + 1}`,
      description: resource.title || '',
      estimatedTimeInMin: Number(resource.estimatedTimeInMin || 0),
      resourceType: resourceTypeName(resource.resourceType).name || 'Resource',
      resourceCategory: getResourceCategory(resource.resourceCategoryType),
      image: getMediaBasePath(
        resource.posterImageUrl,
        'processedMediaBucket'
      ),
      completed: completedResourceIds.includes(resource.resourceId || `${index + 1}`),
    }),
  );

  const fetchPreviousProjectSessionInfo = async () => {
    if (!studentId || !homeworkId || isNaN(Number(homeworkId))) {
      return;
    }

    try {
      const response =
        await LmsAssessmentStudentApiServiceV1ClientWithStatusCodeHandler.getPreviousProjectSessionInfo({
          assessmentId: BigInt(homeworkId),
        });

      const prevSession = response.data;
      dispatch(setPreviousProjectSessionInfo(prevSession));
      dispatch(setActiveProjectSessionId(prevSession?.lastStudentProjectSessionId || undefined));
    } catch (err) {
      console.error('Error fetching previous project session info', err);
      dispatch(setPreviousProjectSessionInfo(undefined));
      dispatch(setActiveProjectSessionId(undefined));
    }
  };

  const fetchProjectStudentResponse = async () => {
    if (!studentId || !homeworkId || isNaN(Number(homeworkId))) {
      return;
    }

    try {
      const response =
        await LmsAssessmentStudentApiServiceV1ClientWithStatusCodeHandler.fetchStudentAssessmentResponse({
          studentId: studentId,
          assessmentId: BigInt(homeworkId),
        });

      dispatch(
        setActiveHomeworkStudentResponse({
          homeworkId: Number(homeworkId),
          response: response.data,
        }),
      );
    } catch (err) {
      console.error('Error fetching project student response', err);
    }
  };

  //~ Ended projects open this page directly (skipping the start cover, which
  //~ normally loads the assessment), so fetch the content here when it's missing.
  const fetchProjectContent = async () => {
    if (!studentId || !homeworkId || isNaN(Number(homeworkId))) {
      return;
    }
    if (activeProjectContent?.assessment) {
      return;
    }

    try {
      const response =
        await LmsAssessmentCommonAPIServiceV1ClientWithStatusCodeHandler.fetchAssessmentContent({
          personId: studentId,
          personType: ProfileRolesEnum.PROFILE_ROLE_STUDENT,
          assessmentId: BigInt(homeworkId),
        });

      dispatch(
        setActiveHomeworkContent({
          homeworkId: Number(homeworkId),
          homeworkContent: updateAssessmentContentWithMarks(response.data),
        }),
      );
    } catch (err) {
      console.error('Error fetching project content', err);
    }
  };

  useEffect(() => {
    const loadActiveProjectDetails = async () => {
      if (!studentId || !homeworkId || isNaN(Number(homeworkId))) {
        setLoading('completed');
        return;
      }

      try {
        setLoading('loading');
        await Promise.all([
          fetchProjectContent(),
          fetchPreviousProjectSessionInfo(),
          fetchProjectStudentResponse(),
        ]);
        setLoading('completed');
      } catch (err) {
        console.error('Error loading active project details', err);
        setLoading('error');
      }
    };

    loadActiveProjectDetails();
  }, [homeworkId, studentId]);
  //~ Close the sitting on the way out, as LessonView does for a lesson session;
  //~ the next Let's start reuses it. Kept in a ref because the header's back
  //~ handler is registered once and would otherwise see stale values.
  const closeProjectSessionRef = useRef<() => void>();
  closeProjectSessionRef.current = () => {
    //~ Submitted or ended work is final; the stored session id is left over
    //~ from before that, so leaving must not write to it.
    if (
      isProjectSubmitted ||
      isProjectEnded ||
      !projectSessionId ||
      !homeworkId ||
      isNaN(Number(homeworkId))
    ) {
      return;
    }
    const allResourcesDone =
      projectResources.length > 0 &&
      projectResources.every((resource) => resource.completed);

    LmsAssessmentStudentApiServiceV1ClientWithStatusCodeHandler.updateStudentProjectSession(
      {
        studentProjectSessionId: projectSessionId,
        assessmentId: BigInt(homeworkId),
        sessionStatus: allResourcesDone
          ? SessionStatusEnum.SESSION_STATUS_COMPLETED
          : SessionStatusEnum.SESSION_STATUS_EXITED,
        endTime: Timestamp.fromDate(new Date()),
      },
    ).catch((error) => console.error('Error closing project session', error));
  };

  const onBackClick = () => {
    closeProjectSessionRef.current?.();
    navigate(Manage_Student_Projects);
    dispatch(setProjectEndPath(undefined));
  };

  /* ------------------------------------------------------------------------ */
  /*                              CALCULATIONS                                */
  /* ------------------------------------------------------------------------ */

  const estimatedTime = projectResources.reduce(
    (total, resource) => total + resource.estimatedTimeInMin,
    0,
  );

  const completedCount = completedResourceIds.length;

  const allResourcesCompleted =
    projectResources.length > 0 &&
    projectResources.every((resource) =>
      completedResourceIds.includes(resource.resourceId),
    );

  const progressPercentage =
    projectResources.length > 0
      ? Math.round((completedCount / projectResources.length) * 100)
      : 0;

  const isProjectSubmitted =
    activeProjectStudentResponse?.assessmentAttemptStatus ===
    TaskStudentAttemptStatusEnum.TASK_STUDENT_STATUS_COMPLETED ||
    projectSubmitted;

  const hasStartedProject =
    Boolean(previous_project_session_info?.lastStudentProjectSessionId) ||
    completedCount > 0 ||
    isProjectSubmitted;

  const isProjectNotStarted =
    !!assessment?.assessmentCommenceDate &&
    Number(assessment.assessmentCommenceDate.seconds) * 1000 > Date.now();

  const allowLateSubmission =
    assessment?.assignmentInfo?.find(
      (info) =>
        info.configName ===
        AssignmentConfigEnum.ASSIGNMENT_CONFIG_ALLOW_LATE_SUBMISSION,
    )?.isEnabled === true;

  const isPastDeadline = isProjectDeadlinePassed(
    assessment?.assessmentTargetDate,
  );

  //~ Past the deadline without late submission only completed resources stay
  //~ viewable; with it, the student keeps working and can still submit.
  const isProjectEnded = isPastDeadline && !allowLateSubmission;
  const isLateSubmission =
    isPastDeadline && allowLateSubmission && !isProjectSubmitted;

  const hideProjectActions =
    isProjectSubmitted || isProjectEnded || isProjectNotStarted;
  const lockedStatusText = isProjectSubmitted
    ? 'Submitted'
    : isProjectNotStarted
      ? `Starts on ${formatSecondsToDateTimeString(
          assessment?.assessmentCommenceDate?.seconds,
        )}`
      : 'Project Ended';

  /* ------------------------------------------------------------------------ */
  /*                              HANDLERS                                    */
  /* ------------------------------------------------------------------------ */

  const ensureProjectSession = async (): Promise<bigint | undefined> => {
    if (!studentId || !homeworkId || isNaN(Number(homeworkId))) {
      return undefined;
    }

    if (projectSessionId) {
      return projectSessionId;
    }

    if (creatingProjectSessionRef.current) {
      return undefined;
    }

    creatingProjectSessionRef.current = true;

    try {
      const projectSession =
        await LmsAssessmentStudentApiServiceV1ClientWithStatusCodeHandler.createStudentProjectSession({
          assessmentId: BigInt(homeworkId),
          schoolId: userInfo?.schoolDetails?.schoolId || 0,
          academicYear: 0,
          classId: userInfo?.classSectionDetails?.classId || 0,
          section: userInfo?.classSectionDetails?.sectionName || '',
          sessionResourceIds: completedResourceIds,
          startTime: Timestamp.fromDate(new Date()),
          sessionStatus: SessionStatusEnum.SESSION_STATUS_STARTED,
          sessionMode: SessionModeEnum.SESSION_MODE_PROJECT,
        });

      const newProjectSessionId = projectSession.studentProjectSessionId;
      if (newProjectSessionId) {
        dispatch(setActiveProjectSessionId(newProjectSessionId));
      }

      return newProjectSessionId;
    } catch (error) {
      console.error('Error creating project session', error);
      return undefined;
    } finally {
      creatingProjectSessionRef.current = false;
    }
  };

  const handleResourceClick = async (resource: {
    id: number;
    resourceId: string;
    title: string;
    resourceType: string;
    estimatedTimeInMin: number;
  }) => {
    if (isProjectNotStarted) {
      return;
    }
    //~ Ended projects can only be reviewed - never resumed into a new session.
    if (isProjectEnded && !completedResourceIds.includes(resource.resourceId)) {
      return;
    }

    //~ Submitted or ended work is final, so open it to review: no session is
    //~ created and nothing is written back, instead of a write the server
    //~ would reject on the first page turn.
    if (isProjectSubmitted || isProjectEnded) {
      navigateToProjectResource(navigate, {
        resourceId: resource.resourceId,
        assessmentId: homeworkId,
        sessionMode: SessionModeEnum.SESSION_MODE_PROJECT,
        viewMode: 'view',
      });
      return;
    }

    const currentProjectSessionId = await ensureProjectSession();

    if (!currentProjectSessionId) {
      return;
    }

    navigateToProjectResource(navigate, {
      resourceId: resource.resourceId,
      projectSessionId: currentProjectSessionId,
      assessmentId: homeworkId,
      sessionMode: SessionModeEnum.SESSION_MODE_PROJECT,
    });
  };

  const handleSubmitProject = async (
    userUploads: PartialMessage<CloudUpload>[],
  ) => {
    if (!studentId || !homeworkId || isNaN(Number(homeworkId))) {
      return;
    }

    try {
      setSubmittingProject(true);
      await LmsAssessmentStudentApiServiceV1ClientWithStatusCodeHandler.updateProjectResourceProgress(
        {
          assessmentId: BigInt(homeworkId),
          baseGroupVersion: previous_project_session_info?.groupVersion,
          submit: true,
          //~ The photo/video evidence attached in the popup. Empty when the
          //~ student skipped both - the fields are optional.
          userUploads: userUploads,
        },
      );

      //~ Pull the new group version and progress back in after submitting
      await fetchPreviousProjectSessionInfo();
      setProjectSubmitted(true);
      setEvidencePopupOpen(false);

      dispatch(
        setToastInfo({
          label: 'Project submitted successfully',
          variant: 'success',
          open: true,
        }),
      );
    } catch (error) {
      console.error('Error submitting project', error);
      const connectError = ConnectError.from(error);

      //~ Aborted = a group member changed the project after we loaded it, so
      //~ our baseGroupVersion is stale. Reload the latest group progress (and
      //~ version) so the next submit goes through, and tell the student why.
      if (connectError.code === Code.Aborted) {
        setEvidencePopupOpen(false);
        await Promise.all([
          fetchPreviousProjectSessionInfo(),
          fetchProjectStudentResponse(),
        ]);
        dispatch(
          setToastInfo({
            label:
              connectError.rawMessage?.trim() ||
              'This project was updated by a group member. We have loaded the latest progress - please review and submit again.',
            variant: 'error',
            open: true,
          }),
        );
        return;
      }

      //~ A teammate already submitted, or the project ended with no late
      //~ submission. Show why and reload, so the page shows that state.
      const isClosed =
        connectError.code === Code.DeadlineExceeded ||
        (connectError.code === Code.FailedPrecondition &&
          /already (been )?submitted/i.test(connectError.rawMessage));
      if (isClosed) {
        setEvidencePopupOpen(false);
        await Promise.all([
          fetchPreviousProjectSessionInfo(),
          fetchProjectStudentResponse(),
        ]);
        dispatch(
          setToastInfo({
            label: connectError.rawMessage,
            variant: 'error',
            open: true,
          }),
        );
        return;
      }

      dispatch(
        setToastInfo({
          label: 'Error Occurred! Unable to submit the project',
          variant: 'error',
          open: true,
        }),
      );
    } finally {
      setSubmittingProject(false);
    }
  };

  const handleStartProject = async () => {
    const currentProjectSessionId = await ensureProjectSession();

    if (!currentProjectSessionId) {
      return;
    }

    const firstUnfinishedResource = projectResources.find(
      (resource) => !completedResourceIds.includes(resource.resourceId),
    );

    if (firstUnfinishedResource) {
      navigateToProjectResource(navigate, {
        resourceId: firstUnfinishedResource.resourceId,
        projectSessionId: currentProjectSessionId,
        assessmentId: homeworkId,
        sessionMode: SessionModeEnum.SESSION_MODE_PROJECT,
      });
    }
  };

  /* ------------------------------------------------------------------------ */
  /*                                TABS                                      */
  /* ------------------------------------------------------------------------ */

  const projectTabs = [
    {
      quantity: projectResources.length.toString(),
      title: 'Resources',
    },
    {
      quantity: estimatedTime.toString(),
      title: 'Mins',
    },
  ];

  useEffect(() => {
    setSelectedFunction(() => onBackClick);
    return () => {
      setSelectedFunction(null);
    };
  }, []);
  /* ------------------------------------------------------------------------ */
  /*                                 UI                                       */
  /* ------------------------------------------------------------------------ */
  if (loading === 'loading') {
    return <ActiveProjectDetailsShimmer />;
  }

  if (loading === 'error') {
    return (
      <NoContentCard
        variant="error"
        icon="error"
        text="Error Occurred! Unable to load project details"
      />
    );
  }

  return (
    <Box sx={styles.root}>
      <Box sx={styles.header}>
        <Box
          sx={{
            width: { xs: '100%', lg: pxTovW(960) },
            wordBreak: 'break-word',
          }}
        >
          <ChapterSelectedCard
            image="/assets/shared-ui/icons/create_project_icon.png"
            // Uncomment when the project icon is added to the bucket, and remove the hardcoded image above.
            // image={
            //   getMediaBasePath(
            //     assessment?.assessmentPosterImgUrl,
            //     'processedMediaBucket',
            //   ) || undefined
            // }
            chapterName={assessment?.chapterName || assessment?.assessmentTitle || ''}
            subject={assessment?.subject || ''}
          />
        </Box>

        <Box
          sx={{
            display: { xs: 'none', md: 'flex' },
            gap: { md: pxToRem(16), lg: pxTovW(16) },
            height: 'max-content',
            width: { md: pxTovW(444) },
            flexShrink: 0,
          }}
        >
          {hideProjectActions ? (
            isProjectNotStarted ? (
              <Box sx={styles.statusEnded}>{lockedStatusText}</Box>
            ) : (
              <></>
            )
          ) : (
            <>
              {!allResourcesCompleted && (<PrimaryButton
                onClick={handleStartProject}
                sx={{
                  boxShadow: `inset 0 0 ${pxTovW(8)} #00602B , 0 0 ${pxTovW(
                    7
                  )} #0AA34F63`,
                  height: { xs: pxToRem(52), md: pxToRem(48), lg: pxTovW(70) },
                }}
                fullWidth
              >
                <Typography
                  variant="bodyText"
                  fontWeight="bold"
                  color={'white'}
                  sx={{ letterSpacing: '1px' }}
                >
                  {hasStartedProject ? 'Resume' : 'Start'}
                </Typography>
              </PrimaryButton>
              )}
              {allResourcesCompleted && (
                <PrimaryButton
                  onClick={() => setEvidencePopupOpen(true)}
                  disabled={submittingProject}
                  sx={{
                    height: { xs: pxToRem(52), md: pxToRem(48), lg: pxTovW(70) },
                  }}
                  fullWidth
                >
                  <Typography
                    variant="bodyText"
                    fontWeight="bold"
                    color={'white'}
                    sx={{ letterSpacing: '1px' }}
                  >
                    {isLateSubmission ? 'Submit (Late)' : 'Submit'}
                  </Typography>
                </PrimaryButton>
              )}
            </>
          )}
        </Box>
      </Box>


      {/* ------------------------------------------------------------------ */}
      {/* BODY                                                               */}
      {/* ------------------------------------------------------------------ */}

      <Box sx={styles.bodySection}>
        {/* -------------------------------------------------------------- */}
        {/* LEFT PANEL                                                      */}
        {/* -------------------------------------------------------------- */}

        <Box sx={styles.leftPanel}>
          {learningOutcomes.length > 0 && (
            <SecondaryButton
              variant="outlined"
              witharrow
              fullWidth
              onClick={async () => {
                await interactionEvent({
                  url: 'Student_active_project_details',
                  context: 'Drop_down',
                  name: 'LEARNING_OUTCOME',
                  isOffline: isOffline,
                });
                setLearningOutcomesOpen(true);
              }}
            >
              <Typography variant="h5" fontWeight="bold" fontFamily="poppins">
                Learning Outcome
              </Typography>
            </SecondaryButton>
          )}

          {/* PROJECT PROGRESS */}

          {largeScreen && (
            <InfoBar
              rootStyles={
                learningOutcomes.length > 0
                  ? { marginTop: { xs: pxToRem(16), md: pxToRem(20), lg: pxTovW(24) } }
                  : undefined
              }
              contentList={[
                {
                  iconName: 'questions',
                  heading: projectResources.length.toString(),
                  subHeading: 'Resources',
                },
                {
                  iconName: 'clock',
                  heading: estimatedTime.toString(),
                  subHeading: 'Minutes',
                },
                // {
                //   iconName: 'questions',
                //   heading: `${progressPercentage}%`,
                //   subHeading: 'Completed',
                // },
              ]}
            />
          )}

          {/* The student's own report for this project: time, score and work.
              Only available once the project has ended (submitted or past deadline). */}
          {homeworkId && (isProjectSubmitted || isProjectEnded) && (
            <Button
              fullWidth
              variant="outlined"
              onClick={() =>
                navigate(
                  `${Student_Gil_Project_Report}?assessmentId=${homeworkId}`
                )
              }
              sx={{
                marginTop: { xs: pxToRem(16), md: pxToRem(18), lg: pxTovW(20) },
                textTransform: 'none',
                fontFamily: 'Lato',
                fontWeight: 700,
                fontSize: { xs: pxToRem(16), md: pxToRem(17), lg: pxTovW(18) },
                color: '#0AA34F',
                border: '1px solid #0AA34F',
                borderRadius: { xs: pxToRem(10), md: pxToRem(10), lg: pxTovW(10) },
                paddingY: { xs: pxToRem(10), md: pxToRem(12), lg: pxTovW(12) },
                '&:hover': {
                  backgroundColor: 'common.white',
                  border: '1px solid #0AA34F',
                },
              }}
            >
              View My Report
            </Button>
          )}

          {/* INSTRUCTIONS */}

          {/* <Box sx={styles.instructionBox}>
            <Typography
              variant="h3"
              fontWeight="bold"
            >
              Project Instructions
            </Typography>

            {project.instructions.map(
              (instruction, index) => (
                <Box
                  key={index}
                  sx={styles.instructionItem}
                >
                  <Box sx={styles.instructionNumber}>
                    {index + 1}
                  </Box>

                  <Typography
                    variant="bodyText"
                    sx={{
                      flex: 1,
                    }}
                  >
                    {instruction}
                  </Typography>
                </Box>
              ),
            )}
          </Box> */}
        </Box>

        {/* -------------------------------------------------------------- */}
        {/* RIGHT PANEL                                                     */}
        {/* -------------------------------------------------------------- */}

        <Box sx={styles.rightPanel}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography variant="h3" sx={{
              fontSize: { xs: pxToRem(21), md: pxToRem(21), lg: pxTovW(24) },
              fontWeight: 400,
              color: "#000000"
            }}>
              Resources
            </Typography>
          </Box>

          {projectResources.length !== 0 ? (
            <Box
              sx={{
                ...styles.cardsContainer,
                // Clear the fixed mobile action bar (Submit sits above Start).
                marginBottom: {
                  xs: pxToRem(
                    !hideProjectActions && allResourcesCompleted ? 140 : 80
                  ),
                  md: pxToRem(10),
                  lg: pxTovW(10),
                },
              }}
            >
              {projectResources.map((resource) => {
                const isCompleted =
                  completedResourceIds.includes(resource.resourceId);

                return (
                  <ContentDetailCard
                    key={resource.resourceId}
                    variant="large"
                    tagName={resource.resourceCategory}
                    image={resource.image}
                    heading={resource.title}
                    onClick={() => handleResourceClick(resource)}
                    showCompletedRibbon={isCompleted}
                    iconDetails={getIconDetails(resource)}
                    showVideoIcon={
                      resource.resourceType === 'Video'
                    }
                    rootStyle={{
                      width: '100%',
                      backgroundColor: 'common.white',
                    }}
                  />
                );
              })}
            </Box>
          ) : (
            <NoContentCard
              variant="info"
              icon="laboratory"
              text="No Project Resources Available"
            />
          )}
        </Box>
      </Box>

      {/* ------------------------------------------------------------------ */}
      {/* MOBILE BUTTON                                                      */}
      {/* ------------------------------------------------------------------ */}

      <Box sx={styles.mobileButton}>
        {!largeScreen && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: pxToRem(10),
            }}
          >
            {hideProjectActions ? (
              <Box sx={isProjectSubmitted ? styles.status : styles.statusEnded}>
                {lockedStatusText}
              </Box>
            ) : (
              <>
                {allResourcesCompleted && (
                  <PrimaryButton
                    onClick={() => setEvidencePopupOpen(true)}
                    disabled={submittingProject}
                    sx={{ height: pxToRem(52), width: '86vw' }}
                  >
                    <Typography
                      variant="bodyText"
                      fontWeight="bold"
                      color={'white'}
                      sx={{ letterSpacing: '1px' }}
                    >
                      {isLateSubmission ? 'SUBMIT (LATE)' : 'SUBMIT'}
                    </Typography>
                  </PrimaryButton>
                )}
                <Box onClick={handleStartProject}>
                  <HwProceedButton
                    buttonTitle={hasStartedProject ? 'RESUME' : 'START'}
                    tabs={projectTabs}
                  />
                </Box>
              </>
            )}
          </Box>
        )}
      </Box>

      <ProjectEvidencePopup
        open={evidencePopupOpen}
        submitting={submittingProject}
        studentId={studentId}
        assessmentId={parseIdParam(homeworkId)}
        sessionId={projectSessionId}
        sectionId={assessment?.sectionId}
        subjectId={assessment?.subjectId}
        handleClose={() => setEvidencePopupOpen(false)}
        handleSubmit={handleSubmitProject}
      />

      <InstructionsPopup
        popupText={learningOutcomes}
        open={learningOutcomesOpen}
        handleClose={() => setLearningOutcomesOpen(false)}
      />
    </Box >
  );
};

export default ActiveProjectDetails;

